import type {
  PoiFilterConfig,
  HeatMapCountryStatuses,
  HeatMapStatusFilterOption,
  PoiFilterOptionsDesign,
} from './map-data-types';
import { statusDataValueToKeyMap } from './heatmap-legend-config';

type ComparatorFunction = (
  filterConfig?: PoiFilterConfig,
  studyValue?: string | number,
  filterValue?: string | number | PoiFilterOptionsDesign[],
) => boolean;

// Map from metadata status values to filter option values
export const heatMapStatusMap = {
  ...statusDataValueToKeyMap,
  all: 'AllStatuses',
} as Record<HeatMapCountryStatuses, HeatMapStatusFilterOption>;

const equalsToComparator: ComparatorFunction = (
  _?: PoiFilterConfig,
  studyValue?: string | number,
  filterValue?: string | number | PoiFilterOptionsDesign[],
) => {
  // Only handle single values for this comparator
  if (Array.isArray(filterValue)) return false;

  return typeof studyValue === 'string' && typeof filterValue === 'string'
    ? studyValue.toLowerCase() === filterValue.toLowerCase()
    : studyValue === filterValue;
};

const designComparator: ComparatorFunction = (
  _?: PoiFilterConfig,
  studyValue?: string | number,
  filterValue?: string | number | PoiFilterOptionsDesign[],
) => {
  // If filterValue is an array, check if studyValue matches any item (OR logic)
  if (Array.isArray(filterValue)) {
    // Empty array means "see all" - show everything
    if (filterValue.length === 0) return true;

    // Check if studyValue matches any of the filter values
    return filterValue.some(val =>
      typeof studyValue === 'string' && typeof val === 'string'
        ? studyValue.toLowerCase() === val.toLowerCase()
        : studyValue === val,
    );
  }

  // Single value comparison (backward compatibility)
  return equalsToComparator(_, studyValue, filterValue);
};

export const poiFilterOptionComparatorMap: Record<string, ComparatorFunction> =
  {
    status: equalsToComparator,
    sex: equalsToComparator,
    riskModellingUsed: equalsToComparator,
    smokersNeverEligible: equalsToComparator,
    targetOutreach: equalsToComparator,
    biomarkersCollected: equalsToComparator,
    smokingCessationOffered: equalsToComparator,
    useCade: equalsToComparator,
    resultsAvailable: equalsToComparator,
    design: designComparator,
    startFinish: (
      _?: PoiFilterConfig,
      studyValue?: string | number,
      filterValue?: string | number | PoiFilterOptionsDesign[],
    ) => {
      if (Array.isArray(filterValue)) return false;
      if (studyValue === '–' || studyValue === 'N/A') return false;

      return (
        studyValue !== undefined &&
        filterValue !== undefined &&
        studyValue >= filterValue
      );
    },
    endFinish: (
      _?: PoiFilterConfig,
      studyValue?: string | number,
      filterValue?: string | number | PoiFilterOptionsDesign[],
    ) => {
      if (Array.isArray(filterValue)) return false;
      if (studyValue === '–' || studyValue === 'N/A') return false;

      return (
        studyValue !== undefined &&
        filterValue !== undefined &&
        studyValue <= filterValue
      );
    },
    continent: equalsToComparator,
    politicalRegion: (
      _?: PoiFilterConfig,
      studyValue?: string | number,
      filterValue?: string | number | PoiFilterOptionsDesign[],
    ) => {
      // Only handle single values for political region
      if (Array.isArray(filterValue)) return false;
      if (typeof studyValue !== 'string' || typeof filterValue !== 'string')
        return false;

      // return simple comparison if study region does not include multiple values
      if (!studyValue.includes(','))
        return studyValue.toLowerCase() === filterValue.toLowerCase();

      return studyValue
        .split(',')
        .map(region => region.trim().toLowerCase())
        .includes(filterValue.toLowerCase());
    },
  } as const;
